// Device-class indicator for comments. We don't store the device on the
// row — `position.viewportWidth` (already captured by the overlay) is the
// truer signal anyway because it reflects what the reviewer was looking
// at, not what hardware they happened to own. A user shrinking their
// browser window to test the mobile layout shows up as "mobile" here,
// which is what the project owner wants to see.

export type DeviceClass = "desktop" | "tablet" | "mobile";

// Thresholds roughly mirror the ViewportToolbar presets (mobile 390 /
// tablet 768) with slack for real-world viewports that fall between
// nominal device sizes.
export function deviceFromViewport(width: number | undefined | null): DeviceClass {
  if (!width || width <= 0) return "desktop";
  if (width < 600) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

export function DeviceIcon({
  device,
  className = "",
}: {
  device: DeviceClass;
  className?: string;
}) {
  const title =
    device === "mobile"
      ? "Posted from a mobile-width viewport"
      : device === "tablet"
        ? "Posted from a tablet-width viewport"
        : "Posted from a desktop-width viewport";
  return (
    <span
      aria-label={title}
      title={title}
      className={`inline-flex items-center text-neutral-400 ${className}`}
    >
      {device === "mobile" ? <MobileSvg /> : device === "tablet" ? <TabletSvg /> : <DesktopSvg />}
    </span>
  );
}

function DesktopSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="12"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M8 20h8M12 16v4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TabletSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <rect
        x="5"
        y="3"
        width="14"
        height="18"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M11 18h2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MobileSvg() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className="h-3.5 w-3.5"
    >
      <rect
        x="7"
        y="2.5"
        width="10"
        height="19"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      <path
        d="M11 18.5h2"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}
